import Vue from 'vue';
import { component, action, getter } from '../../decorators';

import ComponentCreator from '../../helpers/component.creator';
import KeyboardHelper from '../../helpers/keyboard.helper';
import Logger from '../../logger';
import storage from '../../helpers/storage.helpers';

import SOURCES_FOR_EPISODE_MIXIN from '../../mixins/player.sources.mixin';

import Crud, { CLIPS, METADATA, TIMEDATA } from '../../api/crud';
import vtt from '../../api/vtt';

import Player from '../common/player/player.vue';
import Spinner from '../common/spinner/spinner.vue';
import Transcript from '../common/transcript/transcript.vue';
import TimecodeInput from '../common/timecode-input/timecode-input.vue';
import BackBtn from '../common/back-btn/back-btn.vue';
import ClipDialog from '../clip-dialog/clip-dialog.vue';
import Workplace from '../workplace/workplace.vue';
import AddToCart from '../add-to-cart/add-to-cart.vue';
import VideoInfoDialog from '../../customers/video-info-dialog/video-info-dialog.vue';
import KeyLegendDialog from './key-legend-dialog/key-legend-dialog.vue';

import KeyLegend from './key-legend/key-legend.vue';
import MetadataTab from './metadata-tab/metadata-tab.vue';
import RelatedClips from './related-clips/related-clips.vue';
import AddItemDialog from './add-item-dialog/add-item-dialog.vue';

import { EPISODE_LOAD, SHOW_ERROR_NOTIFICATION, SHOW_LOAD_METADATA, ADD_TO_CART, CLIPS_DOWNLOAD } from '../../store/actions';

import { METADATA_TYPES, TIMEDATA_TYPES, MAX_CLIP_DURATION } from '../../config';

import { textContent } from '../common/utils';

import { metadata } from '../../helpers/clip.helpers';
import l10n from '../../transformers/l10n';

import find from 'lodash/find';
import every from 'lodash/every';
import isNil from 'lodash/isNil';
import transform from 'lodash/transform';
import has from 'lodash/has';
import first from 'lodash/first';
import inRange from 'lodash/inRange';
import flatten from 'lodash/flatten';
import each from 'lodash/each';
import reject from 'lodash/reject';
import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import assign from 'lodash/assign';
import isFunction from 'lodash/isFunction';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import some from 'lodash/some';
import tail from 'lodash/tail';
import sortBy from 'lodash/sortBy';
import values from 'lodash/values';

const api = new Crud(CLIPS);
const metadataAPI = new Crud(METADATA);
const timedataAPI = new Crud(TIMEDATA);

const DEFAULT_TIMECODE = 0;
const FIRST_IDX = 0;
const STEP = 1;

const logger = new Logger('ClipMaker');

const STORAGE_GROUP = 'clip_maker';
const STORAGE_FOLLOW = 'follow';

const DIALOGUE_TAB = { type: 'transcript', tk: 'dialogue' };

const TABS = [
   { type: 'hotkeys', tk: 'hotkeys' },
   DIALOGUE_TAB,
   ...TIMEDATA_TYPES
];

@component({
   name: 'ClipMaker',
   components: {
      Player, Workplace, Spinner, KeyLegend, MetadataTab, Transcript, RelatedClips, TimecodeInput, AddToCart,
      BackBtn
   },
   mixins: [SOURCES_FOR_EPISODE_MIXIN],
   watch: {
      episodeId: 'refresh',
      '$route.query.cid': '_handleRouteChanged',
      '$route.query.tcIn': '_handleRouteChanged',
      '$route.query.tcOut': '_handleRouteChanged'
   }
})
export default class ClipMaker {
   info = map(TIMEDATA_TYPES, _emptyInfoCreator)
   loading = false
   episode = null

   tcIn = DEFAULT_TIMECODE
   tcOut = DEFAULT_TIMECODE

   clip = null
   item = null

   currentTime = DEFAULT_TIMECODE
   clips = []
   transcripts = []

   metadataPerEpisode = []
   timedata = []
   highlightedMetadata = transform(TIMEDATA_TYPES, (memo, { type }) => memo[type] = [], {})
   timedataCache = {}

   boundKeys = []
   customKeyListeners = []

   tabs = TABS
   currentTab = DIALOGUE_TAB

   followCTI = false
   _seekingingToSelectedClip = false

   /** @type {Function} */
   @action(EPISODE_LOAD) load
   @action(SHOW_LOAD_METADATA) loadCurrentShow
   @action(ADD_TO_CART) addClipToCart
   @action(SHOW_ERROR_NOTIFICATION) showError

   @getter() getCurrentShowId
   @getter() getCurrentShowName
   @getter() getCurrentUser

   get episodeId() {
      return +this.$route.params.eid;
   }

   get episodeName() {
      return get(this.episode, 'name', '');
   }

   get boundKeysStorageKey() {
      return `hot_key_${this.episodeId}`;
   }

   get timecode() {
      return this.episode.timecode;
   }

   get duration() {
      return this.episode.duration;
   }

   get frameRate() {
      return this.episode.frameRate;
   }

   get canUseUserKeys() {
      return this.tcOut > this.tcIn;
   }

   get showKeyLegend() {
      return this.currentTab.type === 'hotkeys';
   }

   get showClips() {
      return this.currentTab.type === 'Clips';
   }

   get showTranscript() {
      return this.currentTab.type === 'transcript';
   }

   get canRemove() {
      return this.isAdmin && (this.clip || this.item);
   }

   get canEdit() {
      return this.isAdmin && this.clip;
   }

   get isAdmin() {
      return this.getCurrentUser.roles.some(r => r === 'admin');
   }

   get canView() {
      return this.clip;
   }

   get canAdd() {
      return this.tcIn < this.tcOut;
   }

   get canAddToCart() {
      return this.clip;
   }

   get cachedMetadata() {
      const cache = groupBy(this.metadataPerEpisode, 'type');
      return transform(METADATA_TYPES, (memo, { type }) => {
         memo[type] = cache[type] || [];
      }, {});
   }

   get playerOptions() {
      return {
         isHandlingKeyboardEvents: false,
         showPlaybackRate: false
      };
   }

   get usePlayerTimeTitle() {
      return l10n('editor.use_player_time');
   }

   get addBtnTitle() {
      return this.isAdmin ? l10n('editor.add_title') : l10n('editor.add_clip_title');
   }

   get viewBtnTitle() {
      return l10n('editor.create_view');
   }

   get removeSelectedTitle() {
      return l10n('editor.remove_selected');
   }

   get downloadClipbtnTitle() {
      return l10n('cart.download_mxf');
   }

   get editSelectedTitle() {
      return l10n('editor.edit_selected');
   }

   get ctiTitle() {
      return l10n('editor.cti_title');
   }

   get downloadEpisodebtnTitle() {
      return l10n('editor.download_episode_proxy');
   }

   get downloadTitleProxyLink() 
   {
      return `/api/clfinder/titles/${this.episodeId}/proxy/download?language=${storage.get('user', 'language')}`
   }

   selectedType() {
      return this.$refs.workplace ? this.$refs.workplace.selectedType : '';
   }

   timeupdate(time) {
      this.currentTime = time;

      if (this._seekingingToSelectedClip) {
         this.$nextTick(() => this.$refs.workplace.scrollToMarker());
         this._seekingingToSelectedClip = false;
      }
   }

   async refresh() {
      this.loading = true;

      this.episode = await this.load(this.episodeId);

      if (!this.episode) {
         return;
      }

      this.refreshTranscripts();

      const clips = await api.load({ episodeId: this.episodeId });
      this.clips = sortBy(clips, 'tcIn');

      find(this.info, { type: 'Clips' }).info = _normalize(this.clips);

      this.loading = false;

      this.$nextTick(() => {
         const clipId = get(this, '$route.query.cid');
         const tcIn = get(this, '$route.query.tcIn');
         const tcOut = get(this, '$route.query.tcIn');
         if (clipId || tcIn || tcOut) {
            this._handleRouteChanged();
         } else {
            this.changeCurrentTime(this.timecode);
         }
      });
      this.loadTimedata();
   }

   _handleRouteChanged() {
      const clipId = get(this, '$route.query.cid');
      if (clipId) {
         const clip = clipId ? find(this.clips, { id: +clipId }) : null;
         if (clip) {
            this.selectClip(clip);
            this.$refs.player.player._runOnce(() => {
               setTimeout(() => {
                  this.changeCurrentTime(clip.tcIn);
               });
            });
         }
      }

      const tcIn = get(this, '$route.query.tcIn');
      const tcOut = get(this, '$route.query.tcOut');
      if (tcIn && tcOut) {
         this.tcIn = parseFloat(tcIn);
         this.tcOut = parseFloat(tcOut);
         this.$refs.player.player._runOnce(() => {
            setTimeout(() => {
               this.changeCurrentTime(tcIn);
            });
         });
      }
   }

   async loadTimedata() {
      const params = { episodeId: this.episodeId };

      const [metadata, list] = await Promise.all([metadataAPI.load(params), timedataAPI.load(params)]);
      this.metadataPerEpisode = metadata;
      this.timedata = list;
      this._onTimedataChanged();
   }

   _onTimedataChanged() {
      const cacheOfMetadata = this.metadataPerEpisode.reduce((memo, metadata) => {
            memo[metadata.type] = memo[metadata.type] || {};
            memo[metadata.type][metadata.id] = metadata;
            return memo;
      }, {});
      
      this.timedataCache = transform(this.timedata, (memo, timedata) => {
         const { metadataType, metadataId } = timedata;
         const metadata = cacheOfMetadata[metadataType][metadataId];
         if (!metadata) {
            logger.error(`Can't find metadata with id ${metadataId}`);
            return;
         }
         const { name, thumbnailUrl, talentName } = metadata;
         const timedata4showing = { ...timedata, name, thumbnailUrl, talentName };
         (memo[metadataType] = memo[metadataType] || []).push(timedata4showing);
      }, {});
      each(this.timedataCache, (info, type) => {
         find(this.info, { type }).info = this._getNormalizedInfo(type);
      });
   }

   _getNormalizedInfo(type) {
      const all = this.timedataCache[type] || [];
      const highlighted = this.highlightedMetadata[type] || [];
      if (highlighted.length) {
         const filtered = filter(all, ({ metadataId }) => includes(highlighted, metadataId));
         return _normalize(filtered);
      }
      return _normalize(all);
   }

   cutIn(time) {
      this.tcIn = time;
      this.changeCurrentTime(time);

      if (this.isAdmin && this.item) {
         this.item.tcIn = time;
         this._itemWasChanged = true;
         const [origin] = this.timedata.filter(t => t.id === this.item.id);
         if (origin) {
            origin.tcIn = time;
         }
      }
   }

   cutOut(time) {
      this.tcOut = time;
      this.changeCurrentTime(time);

      if (this.isAdmin && this.item) {
         this.item.tcOut = time;
         this._itemWasChanged = true;
         const [origin] = this.timedata.filter(t => t.id === this.item.id);
         if (origin) {
            origin.tcOut = time;
         }
      }
   }

   _rerenderClips() {
      const flow = first(this.info);
      const all = flatten(flow.info);
      flow.info = _normalize([...all]);
   }

   selectClip(clip) {
      this.item = null;
      this.clip = clip;
      this._itemWasChanged = false;

      if (!clip) {
         return;
      }

      this.tcIn = clip.tcIn;
      this.tcOut = clip.tcOut;

      this.$nextTick(() => this.changeCurrentTime(this.tcIn));

      this._seekingingToSelectedClip = !this.followCTI;
   }

   selectItem(item) {
      if (this._itemWasChanged) {
         timedataAPI.save(this.item);
      }

      this._itemWasChanged = false;
      this.clip = null;
      this.item = item;
      if (item) {
         this.changeCurrentTime(item.tcIn);
         this.tcIn = item.tcIn;
         this.tcOut = item.tcOut;
      }
   }

   async refreshTranscripts() {
      if (!this.episode || !this.episode.ccUrl) {
         return;
      }
      try {
         this.transcripts = await vtt.load(this.episode.ccUrl);
      } catch (e) {
         logger.error(e);
      }
   }

   toStartVideo() {
      this.changeCurrentTime(this.timecode);
   }

   toEndVideo() {
      this.changeCurrentTime(this.timecode + this.duration);
   }

   changeCurrentTime(time) {
      this.$refs.player.setCurrentTime(time);
   }

   gotoNextFrame() {
      this.$refs.player.nextFrame();
   }

   gotoPrevFrame() {
      this.$refs.player.prevFrame();
   }

   stopPlay() {
      this.$refs.player.pause();
   }

   togglePlay() {
      this.$refs.player.toggle();
   }

   playForward() {
      this.$refs.player.playForward();
   }

   playBackward() {
      this.$refs.player.playBackward();
   }

   addNewItem() {
      if (this.canAdd) {
         if (!this.isAdmin || this.selectedType() === 'Clips') {
            this.createNewClip();
         } else {
            this.openAddToTimelineDialog();
         }
      }
   }

   createNewClip() {
      const { tcIn, tcOut } = this;

      if (tcOut - tcIn > MAX_CLIP_DURATION) {
         ComponentCreator.alert(l10n('editor.max_duration'));
         return;
      }

      const m = metadata(tcIn, tcOut, this.metadataPerEpisode, this.timedata);
      let { name } = this.episode;
      const options = { clip: { tcIn, tcOut, metadata: m, name, synopsis: '' } };
      this._processClipDialog(options);
   }

   async editClip({ id }) {
      this.loading = true;
      const clip = await api.get(id);
      // const cacheOfMetadata = keyBy(this.metadataPerEpisode, 'id');
      // const metadata = map(clip.metadata, ({ id }) => cacheOfMetadata[id]);

      const confirmText = l10n('editor.save');
      const headerText = l10n('editor.edit_clip');
      const { tcIn, tcOut } = this;

      const m = metadata(tcIn, tcOut, this.metadataPerEpisode, this.timedata);
      const options = { confirmText, headerText, clip: { ...clip, metadata: m } };
      this._processClipDialog(options);
      this.loading = false;
   }

   async _processMetadataForCurrentPeriod(ethalon) {
      const { tcIn, tcOut } = this;
      const old = metadata(tcIn, tcOut, this.metadataPerEpisode, this.timedata);
      const oldAsCache = keyBy(old, _metadataHashCode);
      const ethalonAsCache = keyBy(ethalon, _metadataHashCode);

      const promises = [];

      [...old, ...ethalon].forEach(m => {
         if (!m.id || !has(oldAsCache, _metadataHashCode(m))) {
            promises.push(this._createTimeDateForCurrentPeriog(m));
         } else if (!has(ethalonAsCache, _metadataHashCode(m))) {
            promises.push(this._removeTimeDateFromCurrentPeriog(m));
         }
      });

      await Promise.all(promises);
   }

   _createTimeDateForCurrentPeriog(metadata) {
      return this.addItemToInfo(metadata);
   }

   _removeTimeDateFromCurrentPeriog(metadata) {
      const promises = [];
      const { tcIn, tcOut } = this;
      for (let i = this.timedata.length - 1; i >= 0; i--) {
         const timedata = this.timedata[i];
         if (timedata.metadataId !== metadata.id || timedata.metadataType !== metadata.type) {
            // continue;
         } else if (timedata.tcIn >= tcOut || timedata.tcOut <= tcIn) {
            // continue;
         } else if (timedata.tcIn >= tcIn && timedata.tcOut <= tcOut) {
            const p = this._silentRemove(timedata);
            promises.push(p);
         } else if (timedata.tcIn < tcIn && timedata.tcOut > tcOut) {
            const oldTcOut = timedata.tcOut;
            timedata.tcOut = tcIn;
            let p = timedataAPI.save(timedata);
            promises.push(p);

            p = this._flushTimeData(tcOut, oldTcOut, metadata.id, metadata.type, name, metadata.thumbnailUrl);
            promises.push(p);
         } else if (timedata.tcIn >= tcIn) {
            timedata.tcIn = tcOut;
            const p = timedataAPI.save(timedata);
            promises.push(p);
         } else if (timedata.tcOut <= tcOut) {
            timedata.tcOut = tcIn;
            const p = timedataAPI.save(timedata);
            promises.push(p);
         } else {
            console.log('ooops'); // eslint-disable-line
         }
      }

      return Promise.all(promises);
   }

   async _processClipDialog(options) {
      const searchMetadata = this._loadTimeDataThesaurus;
      const episode = this.episode;
      options = { ...options, searchMetadata, episode };
      // const metadataCache = keyBy(options.clip.metadata, 'id');
      try {
         const { clip } = await ComponentCreator.dialog(ClipDialog, options);
         clip.synopsis = textContent(clip.synopsis);
         const clipMetadata = clip.metadata;
         if (this.isAdmin) {
            await this._processMetadataForCurrentPeriod(clipMetadata);
         }
         clip.metadata = [];
         const stored = await api.save(clip);
         this.metadataPerEpisode = values(keyBy([...clipMetadata, ...this.metadataPerEpisode], _metadataHashCode));
         this._onTimedataChanged();

         assign(clip, stored);

         if (options.clip.id) {
            const storedClip = find(this.clips, { id: options.clip.id });
            assign(storedClip, clip);
         } else {
            this.clips.splice(0, 0, clip); //eslint-disable-line
            this.clip = clip;
         }

         const flow = first(this.info);
         flow.info = _normalize(this.clips);

         // we can select exists clip and then press "add" button
         // so we will have created new one but have as selected another one
         if (get(this.clip, 'id') === clip.id) {
            assign(this.clip, clip);
         }
      } catch (e) {
         //
      }
   }

   async openAddToTimelineDialog() {
      const type = this.selectedType();
      const options = { loader: this._loadTimeDataThesaurus, type, canCreateNewItem: type !== 'character' };
      try {
         let item = await ComponentCreator.dialog(AddItemDialog, options);
         this.addItemToInfo(item);
      } catch (e) {
         // noop
      }
   }

   async _loadTimeDataThesaurus(type, q) {
      if (!q) {
         return [];
      }
      if (type === 'character') {
         return filter(this.metadataPerEpisode, _filterPredicate(type, q));
      }
      const options = { q, type };
      return metadataAPI.load(options);
   }

   async addItemToInfo(item) {
      if (!this.canAdd) {
         return;
      }
      if (!item.id) {
         const stored = await metadataAPI.save(item);
         assign(item, stored);
         this.metadataPerEpisode.push(item);
      }
      const { name, thumbnailUrl } = item;
      const { tcIn, tcOut } = this;

      const metadataId = item.id;
      const metadataType = item.type;

      const contains = some(this.cachedMetadata[metadataType], t => {
         let time = inRange(t.tcIn, tcIn, tcOut) || inRange(t.tcOut, tcIn, tcOut);
         time = time || (inRange(tcIn, t.tcIn, t.tcOut) || inRange(tcOut, t.tcIn, t.tcOut));
         return metadataId === t.metadataId && time;
      });

      if (contains) {
         this.showError(l10n('editor.timeline_dublicate_metadata', { name }));
         return;
      }

      await this._flushTimeData(tcIn, tcOut, metadataId, metadataType, name, thumbnailUrl);
   }

   // eslint-disable-next-line max-params
   async _flushTimeData(tcIn, tcOut, metadataId, metadataType, name, thumbnailUrl) {
      const { episodeId } = this;
      const timedata = {
         metadataId, metadataType, name, tcIn, tcOut, thumbnailUrl, episodeId
      };

      try {
         assign(timedata, await timedataAPI.save({ tcIn, tcOut, metadataId, metadataType, episodeId }));
         this.timedataCache[metadataType] = this.timedataCache[metadataType] || [];
         this.timedataCache[metadataType].push(timedata);
         this.timedata.push(timedata);
         find(this.info, { type: metadataType }).info = this._getNormalizedInfo(metadataType);
      } catch (e) {
         this.showError(l10n('editor.timeline_store_error', { name }));
      }
   }

   async proxyRemove(item) {
      if (!this.canRemove) {
         return;
      }

      const message = this.clip && this.clip.isLocked
         ? l10n('editor.confirm_remove_locked_clip')
         : l10n('editor.confirm_remove_metadata');

      const res = await ComponentCreator.confirm(message);
      if (!res) {
         return;
      }

      this.item = null;
      this.clip = null;

      this._silentRemove(item);
   }

   async _silentRemove(item) {
      let type, _api;
      if (has(item, 'metadataType')) {
         type = item.metadataType;
         _api = timedataAPI;
      } else {
         type = l10n('editor.clips');
         _api = api;
      }

      try {
         await _api.remove(item);
      } catch (e) {
         this.showError(l10n('editor.error_when_removing'));
         logger.error(e);
         return;
      }

      if (has(item, 'metadataType')) {
         this.timedata = reject(this.timedata, { id: item.id });
         this.timedataCache[type] = reject(this.timedataCache[type], { id: item.id });
         find(this.info, { type }).info = this._getNormalizedInfo(type);
      } else {
         this.clips = reject(this.clips, { id: item.id });
         find(this.info, { type }).info = _normalize(this.clips);
      }
   }

   openClipInfo(clip) {
      if (this.isAdmin) {
         this.editClip(clip);
      } else {
         this.viewClip(clip);
      }
   }

   proxyDispatch([event, ...args]) {
      const map = {
         'add-new-item': 'addNewItem',
         'select-clip': 'selectClip',
         'select-item': 'selectItem',
         'remove-item': 'proxyRemove',
         'edit-clip': 'editClip',
         'cut-in': 'cutIn',
         'cut-out': 'cutOut',
         'prev-frame': 'gotoPrevFrame',
         'next-frame': 'gotoNextFrame',
         'stop-play': 'stopPlay',
         'toggle-play': 'togglePlay',
         'play-forward': 'playForward',
         'play-backward': 'playBackward',
         'prev-clip': 'selectPrevClip',
         'next-clip': 'selectNextClip',
         'open-clip-info': 'openClipInfo',
         'create-new-clip': 'createNewClip',
         'change-current-time': 'changeCurrentTime',
         'jump-to-start': 'toStartVideo',
         'jump-to-end': 'toEndVideo'
      };

      const method = this[map[event]];
      if (isFunction(method)) {
         method.apply(this, args);
      } else {
         logger.error(`Not found listener for event "${event}"`);
      }
   }

   refreshBoundKeys(keys) {
      this._removeCustomListeners();
      this.boundKeys = [];
      each(keys, ({ code, item }) => {
         const listener = KeyboardHelper.keydown(() => this.addItemToInfo(item), code);
         this.customKeyListeners.push(listener);
         this.boundKeys.push({ code, item });
      });
      storage.set(STORAGE_GROUP, this.boundKeysStorageKey, this.boundKeys);
   }

   _removeCustomListeners() {
      each(this.customKeyListeners, fn => fn());
      this.customKeyListeners.length = 0;
   }

   changeTab(tab) {
      this.currentTab = tab;

      if (this.$refs.workplace && some(TIMEDATA_TYPES, { type: tab.type })) {
         this.$refs.workplace.selectType(tab.type);
      }
   }

   highlightMetadata(list) {
      const { type } = this.currentTab;
      this.highlightedMetadata[type] = list;
      find(this.info, { type }).info = this._getNormalizedInfo(type);
   }

   toggleCTI() {
      this.followCTI = !this.followCTI;
      storage.set(STORAGE_GROUP, STORAGE_FOLLOW, this.followCTI);
   }

   async viewClip({ id }) {
      const options = { episodeId: this.episodeId, clipId: id };
      try {
         await ComponentCreator.dialog(VideoInfoDialog, options);
      } catch (e) {
         // console.error(e);
      }
   }

   async showKeyLegendPopup() {
      try {
         await ComponentCreator.dialog(KeyLegendDialog);
      } catch (e) {
         // noop
      }
   }

   selectNextClip() {
      const { clip, clips } = this;
      const len = clips.length;
      if (!len) {
         return;
      }
      let idx = clips.indexOf(clip);
      idx += STEP;
      idx = idx === len ? FIRST_IDX : idx;
      this.selectClip(clips[idx]);
   }

   selectPrevClip() {
      const { clip, clips } = this;
      if (!clips.length) {
         return;
      }
      let idx = clips.indexOf(clip);
      idx = idx || clips.length;
      idx -= STEP;
      this.selectClip(clips[idx]);
   }

   created() {
      const showId = this.$route.params.mid;

      const followCTI = storage.get(STORAGE_GROUP, STORAGE_FOLLOW);
      this.followCTI = typeof followCTI === 'boolean' ? followCTI : true;

      if (!this.isAdmin) {
         this.tabs = tail(TABS);
      }

      this.loadCurrentShow(showId);
      this.refresh();
      const keys = storage.get(STORAGE_GROUP, this.boundKeysStorageKey) || [];
      this.refreshBoundKeys(keys);
   }

   downloadClip(clipId) {
      this.$store.dispatch(CLIPS_DOWNLOAD, { rootTitleId: this.getCurrentShowId, clipId, language: storage.get('user', 'language') });
   }
}

function _normalize(items) {
   items = sortBy(items, _sortCondition);
   return transform(items, _normalizeReducer, []);
}

function isNotOverlapping(item) {
   return function _it(lane) {
      return every(lane, t => t.tcIn > item.tcOut || t.tcOut < item.tcIn);
   };
}

function _normalizeReducer(memo, item) {
   let lane = find(memo, isNotOverlapping(item));
   if (isNil(lane)) {
      lane = [];
      memo.push(lane);
   }
   lane.push(item);
}

function _filterPredicate(type, search) {
   search = search.toLowerCase();
   return i => i.type === type && includes(i.name.toLowerCase(), search);
}

function _emptyInfoCreator({ type }) {
   return { type, info: [] };
}

function _sortCondition(item) {
   return item.id ? +item.id : Number.MAX_VALUE;
}

function _metadataHashCode(m) {
   return `${m.type}__${m.id}`;
}
